import { Box, Button, FormControl, FormLabel, Radio, RadioGroup, Select, Stack, useToast } from '@chakra-ui/react'
import { yupResolver } from "@hookform/resolvers/yup"
import { Product } from 'BrokerTsLib/src/enums'
import moment from 'moment'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Datepicker } from '../../../components/Datepicker'
import { InputGroup } from '../../../components/InputGroup'
import { useSendStatisticEmail } from '../../../hooks/useOrigApp'
import { Export } from '../../../icons'
import { ExportWrapper } from '../ExportWrapper'
const carkulkaUrl = process.env.REACT_APP_CARKULKA_URL

enum StatisticTypes {
  Contracted = 1,
  Insurances = 4,
  InsurancesEmail = 5,
  InstallmentByInsurersAndMakers = 20,
  InstallmentByMakers = 21,
  InstallmentByInsurers = 22
}

enum FileFormat {
  Pdf = 1,
  Xlsx = 2
}

enum DateTypes {
  ContractedDate = 1,
  InsuranceStartDate = 2
}

export interface ContractsCarExportFormValues {
  dateFrom: Date
  dateTo: Date
  selectBy: DateTypes
  formatType: FileFormat
  exportType: StatisticTypes
  email: string
}

interface ContractsExportFormProps {
  product: Product
}

const schema = yup.object().shape({
  email: yup.string()
    .when('exportType', {
      is: (exportType: StatisticTypes) => exportType == StatisticTypes.InsurancesEmail,
      then: () => yup.string().nullable().required('Zadejte email'),
      otherwise: () => yup.string().nullable()
    }),
});

const getAvailableStatisticsTypes = (product: Product) => {
  const arr: {
    type: StatisticTypes,
    desc: string
  }[] = []

  if (product === Product.VehicleInsurance) {
    arr.push({ type: StatisticTypes.Contracted, desc: 'Přehled sjednaných smluv' })
    arr.push({ type: StatisticTypes.InstallmentByInsurers, desc: 'Přehled pojistného dle pojišťoven' })
    arr.push({ type: StatisticTypes.InstallmentByMakers, desc: 'Přehled pojistného dle značek' })
    arr.push({ type: StatisticTypes.InstallmentByInsurersAndMakers, desc: 'Přehled pojistného dle pojišťoven a značek' })
  }

  arr.push({ type: StatisticTypes.Insurances, desc: 'Přehled pojistek' })
  arr.push({ type: StatisticTypes.InsurancesEmail, desc: 'Přehled pojistek emailem' })

  return arr
}

export const ContractsExportForm = ({ product }: ContractsExportFormProps) => {
  const { mutateAsync: sendEmail, isLoading: isSending } = useSendStatisticEmail()
  const toast = useToast()

  const types = getAvailableStatisticsTypes(product)

  const methods = useForm<ContractsCarExportFormValues>({
    defaultValues: {
      dateFrom: new Date(new Date().getFullYear(), new Date().getMonth() - 1),
      dateTo: new Date(),
      exportType: types[0].type,
      selectBy: DateTypes.ContractedDate,
      formatType: FileFormat.Xlsx,
    },
    resolver: yupResolver(schema) as any,
  })
  const { control, register, getValues, setValue, watch, trigger, formState: { errors } } = methods


  const dateFrom = watch('dateFrom')
  const dateTo = watch('dateTo')
  const email = watch('email')
  const exportType = watch('exportType')
  const formatType = watch('formatType')
  const selectBy = watch('selectBy')

  const url = `${carkulkaUrl}/InsuranceStatistics/GetStatistic?` +
    `StatisticType=${exportType?.toString()}` +
    `&DateType=${selectBy?.toString()}` +
    `&fileformat=${formatType?.toString()}` +
    `&Email=${encodeURIComponent(email ?? '')}` +
    `&DateFrom=${moment(dateFrom).format("YYYY-MM-DD")}` +
    `&DateTo=${moment(dateTo).format("YYYY-MM-DD")}` +
    `&Product=${product.toString()}`

  const isEmail = exportType == StatisticTypes.InsurancesEmail

  return (
    <FormProvider {...methods}>
      <form>
        <ExportWrapper>
          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateFrom">Datum od</FormLabel>

            <Datepicker
              date={getValues('dateFrom')}
              onDateChange={(date) => setValue('dateFrom', date!)}
            />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="dateTo">Datum do</FormLabel>

            <Datepicker
              date={getValues('dateTo')}
              onDateChange={(date) => setValue('dateTo', date!)}
            />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="selectBy">Výběr podle</FormLabel>

            <Controller
              name="selectBy"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup value={value.toString()} onChange={e => onChange(parseInt(e))}>
                  <Stack direction="column" spacing={4}>
                    <Radio value={DateTypes.ContractedDate.toString()}>Data sjednání smlouvy</Radio>
                    <Radio value={DateTypes.InsuranceStartDate.toString()}>Data počátku pojištění</Radio>
                  </Stack>
                </RadioGroup>
              )}
            />
          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="formatType">Formát výstupu</FormLabel>

            <Controller
              name="formatType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioGroup value={value.toString()} onChange={e => onChange(parseInt(e))}>
                  <Stack direction="column" spacing={4}>
                    <Radio value={FileFormat.Xlsx.toString()}>XLS</Radio>
                    <Radio value={FileFormat.Pdf.toString()}>PDF</Radio>
                  </Stack>
                </RadioGroup>
              )}
            />

          </FormControl>

          <FormControl variant="fullWidth">
            <FormLabel htmlFor="exportType">Typ přehledu</FormLabel>

            <Select {...register('exportType')}>
              {types.map(x => (
                <option key={x.type.toString()} value={x.type.toString()}>{x.desc}</option>
              ))}

            </Select>
          </FormControl>

          <FormControl variant="fullWidth" isInvalid={!!errors.email}>
            <FormLabel htmlFor="email">E-mail</FormLabel>

            <InputGroup
              id="email"
              placeholder="Zadejte e-mail, na který export odešleme"
              isDisabled={!isEmail}
              errors={errors.email}
              inputMode="email"
              {...register('email')}
            />
          </FormControl>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 4,
              rowGap: 4,
              alignItems: 'end',
              justifyContent: 'end',
              gridColumn: { base: '', md: '4 / 5' },
            }}
          >
            {!isEmail &&
              <Button
                colorScheme="primary"
                onClick={() => window.open(url)}
              >
                <Export />
                Exportovat
              </Button>}

            {isEmail &&
              <Button
                type='button'
                colorScheme="primary"
                isLoading={isSending}
                loadingText='Odesílám...'
                onClick={() => {
                  trigger('email').then((valid) => {
                    if (!valid) return

                    sendEmail(url)
                      .then(() => {
                        toast({
                          title: 'Email odeslán',
                          status: 'success',
                          duration: 5000,
                          isClosable: true,
                        })
                      })
                      .catch((e) => {
                        toast({
                          title: 'Zprávu se nepodařilo odeslat!',
                          description: e.response.status === 400 ? e.response.statusText : '',
                          status: 'error',
                          duration: 5000,
                          isClosable: true,
                        })
                      })
                  })
                }}
              >
                <Export />

                Odeslat
              </Button>
            }
          </Box>
        </ExportWrapper>
      </form>
    </FormProvider>
  )
}
